/** @module Auth0Tenant */
/* eslint-disable import/extensions */
import {
  ENVIRONMENTS,
  DEV_ENVIRONMENTS,
  LOCAL_ENVIRONMENTS,
  parseTenantName,
  isTenantInClass,
  isTenantForEnvironment,
  isValidTenant,
  deriveAuth0DomainForTenant,
  deriveCustomDomainForTenant,
  getAllTenantClasses,
} from '../config/auth0-tenant-registry.mjs';

/**
 * Auth0 Tenant utility class.
 *
 * Provides methods to identify tenant class, environment, and derive domains.
 * All logic delegates to auth0-tenant-registry.mjs for centralized configuration.
 *
 * This class is GENERIC - adding a new tenant class to the registry automatically
 * makes it available here without any code changes.
 */
class Auth0Tenant {
  constructor(tenant) {
    this._tenant = tenant;
    this._parsed = parseTenantName(tenant);
  }

  get id() {
    return this._tenant;
  }

  // ==========================================
  // Tenant class checks (generic)
  // ==========================================

  /**
   * Check if this tenant belongs to a specific tenant class.
   * @param {string} tenantClass - The tenant class to check (e.g., 'shipstation')
   * @returns {boolean}
   */
  isInTenantClass(tenantClass) {
    return isTenantInClass(this._tenant, tenantClass);
  }

  // ==========================================
  // Environment checks (generic)
  // ==========================================

  /**
   * Check if this tenant is for a specific environment.
   * @param {string} environment - The environment to check (e.g., 'prod', 'local-z2')
   * @returns {boolean}
   */
  isForEnvironment(environment) {
    return isTenantForEnvironment(this._tenant, environment);
  }

  /**
   * Check if this tenant is for the prod environment.
   * @returns {boolean}
   */
  get isProdTenant() {
    return this._parsed?.environment === 'prod';
  }

  /**
   * Check if this tenant is for the stage environment.
   * @returns {boolean}
   */
  get isStageTenant() {
    return this._parsed?.environment === 'stage';
  }

  /**
   * Check if this tenant is for the dev environment.
   * @returns {boolean}
   */
  get isDevTenant() {
    return this._parsed?.environment === 'dev';
  }

  /**
   * Check if this tenant is for the local environment.
   * @returns {boolean}
   */
  get isLocalTenant() {
    return this._parsed?.environment === 'local';
  }

  /**
   * Check if this tenant is for a dev environment (dev, local, local-z*).
   * Works for ALL tenant classes.
   */
  get isTenantForDev() {
    if (!this._parsed) return false;
    return DEV_ENVIRONMENTS.includes(this._parsed.environment);
  }

  /**
   * Check if this tenant is for a local environment (local, local-z*).
   * Works for ALL tenant classes.
   */
  get isLocalEnv() {
    if (!this._parsed) return false;
    return LOCAL_ENVIRONMENTS.includes(this._parsed.environment);
  }

  /**
   * Check if a candidate string is a valid tenant name.
   * @param {string} candidate - The string to check
   * @returns {boolean}
   */
  static isTenant(candidate) {
    return isValidTenant(candidate);
  }

  // ==========================================
  // Display utilities
  // ==========================================

  get tenantDisplayName() {
    return this._tenant.toUpperCase().replace('SHIPSTATION-', '');
  }

  // ==========================================
  // Domain derivation (using registry)
  // ==========================================

  get auth0BasedDomain() {
    return this.getAuth0BasedDomain();
  }

  /**
   * Get the Auth0 tenant domain (e.g., https://shipstation-local-z2.us.auth0.com)
   * @param {boolean} includeProtocol - Whether to include https:// prefix
   * @returns {string}
   */
  getAuth0BasedDomain(includeProtocol = true) {
    const domain = deriveAuth0DomainForTenant(this._tenant);
    if (!domain) {
      // Fallback for unrecognized tenants (shouldn't happen in practice)
      const protocolStr = includeProtocol ? 'https://' : '';
      return `${protocolStr}${this._tenant}.auth0.com`;
    }
    return includeProtocol ? `https://${domain}` : domain;
  }

  /**
   * Get the custom signin domain (e.g., signin-local-z2.shipstation.com)
   * @returns {string}
   */
  resolveDomain = () => {
    const domain = deriveCustomDomainForTenant(this._tenant);
    if (!domain) {
      // Fallback for unrecognized tenants — preserve current TLD for ZDE/DDE compatibility
      const baseDomain = typeof window !== 'undefined' && window.location.hostname.endsWith('.sslocal.com')
        ? 'sslocal.com'
        : 'shipstation.com';
      return `signin${this._tenant.replace('shipstation', '')}.${baseDomain}`;
    }
    return domain;
  };

  /**
   * Get the token issuer URL for this tenant.
   * @returns {string}
   */
  get tokenIssuer() {
    const domain = this.resolveDomain();
    return `https://${domain}/`;
  }

  // ==========================================
  // Parsed tenant info (from registry)
  // ==========================================

  /**
   * Get the tenant class (e.g., 'shipstation' or 'shipstation-partners').
   * @returns {string | null}
   */
  get tenantClass() {
    return this._parsed?.tenantClass ?? null;
  }

  /**
   * Get the environment (e.g., 'prod', 'local-z2').
   * @returns {string | null}
   */
  get environment() {
    return this._parsed?.environment ?? null;
  }

  // ==========================================
  // Static utilities
  // ==========================================

  /**
   * Get all available environments.
   * @returns {string[]}
   */
  static get environments() {
    return [...ENVIRONMENTS];
  }

  /**
   * Get all registered tenant classes.
   * @returns {string[]}
   */
  static get tenantClasses() {
    return getAllTenantClasses();
  }
}

export default Auth0Tenant;
